import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Shield, BarChart3, Globe } from "lucide-react";

export default function LoginPage() {
  const { user } = useContext(AuthContext);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex login-bg" data-testid="login-page">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-950 text-white flex-col justify-between p-12 relative overflow-hidden noise-bg">
        <div className="relative z-10">
          <div className="mb-16">
            <img src="/gm-pulse-logo.png" alt="GM Pulse" data-testid="login-logo" className="h-11 w-auto" />
          </div>

          <h1
            className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Prospect smarter.
            <br />
            <span className="text-indigo-400">Close faster.</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-md leading-relaxed">
            AI-powered prospecting and audit platform for digital marketing agencies.
            Find leads, audit their digital presence, and close deals.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-6 mt-12">
          {[
            { icon: Globe, label: "Presence Audits", desc: "12+ directories" },
            { icon: BarChart3, label: "Website Scoring", desc: "25+ checks" },
            { icon: Shield, label: "CRM Sync", desc: "Monday & GHL" },
          ].map((f, i) => (
            <div
              key={i}
              className="p-4 rounded-lg bg-white/5 border border-white/10"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <f.icon className="w-5 h-5 text-indigo-400 mb-2" />
              <p className="text-sm font-semibold">{f.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Decorative gradient */}
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-indigo-600/10 blur-3xl" />
        <div className="absolute top-1/4 -left-16 w-64 h-64 rounded-full bg-teal-600/5 blur-3xl" />
      </div>

      {/* Right panel - Login */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="lg:hidden mb-10">
            <img src="/gm-pulse-logo.png" alt="GM Pulse" className="h-8 w-auto" />
          </div>

          <h2
            className="text-2xl font-bold tracking-tight mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Welcome back
          </h2>
          <p className="text-muted-foreground mb-8">
            Sign in to access your prospecting dashboard
          </p>

          <Button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="w-full h-12 bg-slate-950 hover:bg-slate-800 text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-transform active:scale-[0.98]"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="text-xs text-muted-foreground text-center mt-6">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
